// src/components/review/ReviewPage.jsx
// 공개 리뷰 작성 페이지 — 앱/회원가입 불필요

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fsGetReviewContext, fsSubmitReview } from '../../services/firebase';

export default function ReviewPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);       // { pensionName, customerName, used }
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('form');   // 'form' | 'done' | 'error' | 'used'

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [privateFeedback, setPrivateFeedback] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fsGetReviewContext(token)
      .then(data => {
        if (!data) { setStep('error'); }
        else if (data.used) { setStep('used'); }
        else { setCtx(data); }
      })
      .catch(() => setStep('error'))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSubmit() {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      await fsSubmitReview(token, { rating, comment, privateFeedback, marketingConsent });
      setStep('done');
    } catch {
      alert('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  }

  const containerStyle = {
    maxWidth: 480, margin: '0 auto', minHeight: '100dvh',
    background: '#fafafa', display: 'flex', flexDirection: 'column',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  if (loading) {
    return (
      <div style={{ ...containerStyle, alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <div style={{ fontSize: 36 }}>🏡</div>
        <div style={{ fontSize: 15, color: '#6b7280' }}>불러오는 중...</div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div style={{ ...containerStyle, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
        <div style={{ fontSize: 48 }}>😥</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>유효하지 않은 링크입니다</div>
        <div style={{ fontSize: 14, color: '#6b7280', textAlign: 'center' }}>링크가 만료되었거나 올바르지 않습니다.</div>
      </div>
    );
  }

  if (step === 'used') {
    return (
      <div style={{ ...containerStyle, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
        <div style={{ fontSize: 48 }}>✅</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>이미 리뷰를 작성하셨습니다</div>
        <div style={{ fontSize: 14, color: '#6b7280', textAlign: 'center' }}>소중한 후기를 남겨주셔서 감사합니다 🙏</div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div style={{ ...containerStyle, alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
        <div style={{ fontSize: 64 }}>⭐</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>감사합니다!</div>
        <div style={{ fontSize: 15, color: '#6b7280', textAlign: 'center', lineHeight: 1.7 }}>
          {ctx?.customerName ? `${ctx.customerName}님의 ` : ''}소중한 후기가 전달되었습니다.<br />
          덕분에 더 좋은 숙소가 될게요 🙏
        </div>
        {marketingConsent && (
          <div style={{
            marginTop: 8, padding: '12px 20px', borderRadius: 12,
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            fontSize: 13, color: '#15803d', fontWeight: 600, textAlign: 'center',
          }}>
            ✓ 할인·이벤트 안내 수신에 동의해주셨습니다
          </div>
        )}
      </div>
    );
  }

  const displayRating = hovered || rating;

  return (
    <div style={containerStyle}>
      {/* 헤더 */}
      <div style={{
        padding: '20px 20px 16px',
        background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
        color: 'white',
      }}>
        <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>리뷰 요청</div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{ctx?.pensionName || '숙소'}</div>
        {ctx?.customerName && (
          <div style={{ fontSize: 14, opacity: 0.9, marginTop: 4 }}>
            {ctx.customerName}님, 이용해주셔서 감사합니다 🙏
          </div>
        )}
      </div>

      {/* 본문 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 100px' }}>

        {/* 별점 */}
        <div style={{
          background: 'white', borderRadius: 16, padding: '20px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 14,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#111827' }}>
            이번 방문은 어떠셨나요? <span style={{ color: '#dc2626' }}>*</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 10 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                onMouseEnter={() => setHovered(n)}
                onMouseLeave={() => setHovered(0)}
                onClick={() => setRating(n)}
                style={{
                  fontSize: 42, background: 'none', border: 'none', cursor: 'pointer',
                  transition: 'transform 0.1s',
                  transform: n <= displayRating ? 'scale(1.15)' : 'scale(1)',
                  filter: n <= displayRating ? 'none' : 'grayscale(1) opacity(0.3)',
                  padding: '2px',
                }}
              >⭐</button>
            ))}
          </div>
          {rating > 0 && (
            <div style={{ textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#d97706' }}>
              {['', '아쉬웠어요', '그저 그랬어요', '보통이었어요', '좋았어요', '아주 좋았어요!'][rating]}
            </div>
          )}
        </div>

        {/* 한줄평 */}
        <div style={{
          background: 'white', borderRadius: 16, padding: '20px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 14,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: '#111827' }}>한줄평</div>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="숙소에 대한 솔직한 후기를 남겨주세요"
            maxLength={200}
            style={{
              width: '100%', minHeight: 90, padding: '12px',
              border: '1.5px solid #e5e7eb', borderRadius: 10,
              fontSize: 14, resize: 'none', boxSizing: 'border-box',
              fontFamily: 'inherit', lineHeight: 1.6, outline: 'none',
            }}
            onFocus={e => e.target.style.borderColor = '#22c55e'}
            onBlur={e => e.target.style.borderColor = '#e5e7eb'}
          />
          <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
            {comment.length}/200
          </div>
        </div>

        {/* 사장님께 하고 싶은 말 */}
        <div style={{
          background: 'white', borderRadius: 16, padding: '20px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 14,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: '#111827' }}>
            사장님께 하고 싶은 말
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
            불편했던 점, 개선 사항 등을 자유롭게 남겨주세요 (선택사항 · 비공개)
          </div>
          <textarea
            value={privateFeedback}
            onChange={e => setPrivateFeedback(e.target.value)}
            placeholder="사장님만 보실 수 있습니다"
            maxLength={500}
            style={{
              width: '100%', minHeight: 80, padding: '12px',
              border: '1.5px solid #e5e7eb', borderRadius: 10,
              fontSize: 14, resize: 'none', boxSizing: 'border-box',
              fontFamily: 'inherit', lineHeight: 1.6, outline: 'none',
            }}
            onFocus={e => e.target.style.borderColor = '#22c55e'}
            onBlur={e => e.target.style.borderColor = '#e5e7eb'}
          />
        </div>

        {/* 마케팅 동의 */}
        <div style={{
          background: 'white', borderRadius: 16, padding: '18px 20px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 14,
        }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
            <div
              onClick={() => setMarketingConsent(v => !v)}
              style={{
                width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
                border: `2px solid ${marketingConsent ? '#16a34a' : '#d1d5db'}`,
                background: marketingConsent ? '#16a34a' : 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s', cursor: 'pointer',
              }}
            >
              {marketingConsent && <span style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>✓</span>}
            </div>
            <div onClick={() => setMarketingConsent(v => !v)}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 3 }}>
                다음 방문 시 할인/이벤트 안내를 받으시겠습니까?
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
                동의 시 {ctx?.pensionName || '숙소'}에서 특별 할인 및 이벤트 소식을<br />
                문자로 받아보실 수 있습니다 (언제든 거부 가능)
              </div>
            </div>
          </label>
        </div>

      </div>

      {/* 제출 버튼 (하단 고정) */}
      <div style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480,
        padding: '12px 20px 28px', background: 'white',
        borderTop: '1px solid #f3f4f6',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
      }}>
        <button
          onClick={handleSubmit}
          disabled={rating === 0 || submitting}
          style={{
            width: '100%', padding: '15px', borderRadius: 14, border: 'none',
            fontSize: 16, fontWeight: 800, cursor: rating === 0 ? 'not-allowed' : 'pointer',
            background: rating > 0 && !submitting
              ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
              : '#e5e7eb',
            color: rating > 0 && !submitting ? 'white' : '#9ca3af',
            boxShadow: rating > 0 ? '0 4px 16px rgba(34,197,94,0.35)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          {submitting ? '제출 중...' : rating === 0 ? '별점을 선택해주세요' : '⭐ 리뷰 제출하기'}
        </button>
      </div>
    </div>
  );
}
